import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLResponsiveDesign = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Responsive Design</div>
                <h1>Responsive Design Techniques & Patterns</h1>
                <p className="hero-lead">
                    Master responsive web design patterns. Learn flexible grids, responsive typography,
                    container queries, and modern CSS techniques for building websites that adapt to any device.
                </p>
            </div>

            <div className="content-section">
                <h2>Responsive Design Principles</h2>
                <p>
                    Responsive web design is an approach that makes web pages render well on all devices
                    and screen sizes by using flexible layouts, images, and CSS media queries.
                </p>

                <div className="principles-grid">
                    <div className="principle-card">
                        <h3> Fluid Grids</h3>
                        <p>Use relative units (%, fr) instead of fixed pixels</p>
                    </div>
                    <div className="principle-card">
                        <h3> Flexible Images</h3>
                        <p>Images scale within containing elements</p>
                    </div>
                    <div className="principle-card">
                        <h3> Media Queries</h3>
                        <p>Apply styles based on device characteristics</p>
                    </div>
                    <div className="principle-card">
                        <h3> Breakpoints</h3>
                        <p>Design for common device sizes</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Common Breakpoints</h2>

                <div className="code-example">
                    <CodeBlock language="css" code={`/* Mobile first approach */

/* Extra small devices (phones, less than 576px) */
/* No media query - this is the default */

/* Small devices (landscape phones, 576px and up) */
@media (min-width: 576px) {"{ }"}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {"{ }"}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {"{ }"}

/* Extra large devices (large desktops, 1200px and up) */
@media (min-width: 1200px) {"{ }"}

/* 2K screens */
@media (min-width: 1920px) {"{ }"}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Flexible Grid with Flexbox</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        .container {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            padding: 20px;
        }
        
        .column {
            flex: 1 1 300px; /* grow, shrink, basis */
            background: #4CAF50;
            padding: 20px;
            color: white;
        }
        
        /* Automatically wraps to new lines when space runs out */
    </style>
</head>
<body>
    <div class="container">
        <div class="column">Column 1</div>
        <div class="column">Column 2</div>
        <div class="column">Column 3</div>
    </div>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>CSS Grid for Responsive Layouts</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
.grid-container {
    display: grid;
    
    /* Auto-fit: creates as many columns as fit */
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    padding: 20px;
}

.grid-item {
    background: #2196F3;
    padding: 20px;
    color: white;
}

/* Result: Automatically responsive!
   - 1 column on mobile (&lt; 300px width)
   - 2 columns on tablet
   - 3+ columns on desktop
   - No media queries needed! */
</style>

<div class="grid-container">
    <div class="grid-item">Item 1</div>
    <div class="grid-item">Item 2</div>
    <div class="grid-item">Item 3</div>
    <div class="grid-item">Item 4</div>
    <div class="grid-item">Item 5</div>
    <div class="grid-item">Item 6</div>
</div>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Typography</h2>

                <h3>Fluid Typography with clamp()</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
/* clamp(minimum, preferred, maximum) */
h1 {
    font-size: clamp(1.5rem, 5vw, 3rem);
    /* Min: 24px, scales with viewport, Max: 48px */
}

h2 {
    font-size: clamp(1.25rem, 3vw, 2rem);
}

p {
    font-size: clamp(1rem, 2vw, 1.125rem);
}

/* Responsive line height */
.content {
    line-height: clamp(1.4, 1.5vw, 1.8);
}
</style>`} />
                </div>

                <h3>Viewport Units</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
/* vw = viewport width percentage */
h1 {
    font-size: 5vw; /* 5% of viewport width */
}

/* Combine with calc() for better control */
h1 {
    font-size: calc(16px + 2vw);
    /* Base 16px + 2% of viewport width */
}

/* Use with min/max for constraints */
h1 {
    font-size: max(24px, min(5vw, 48px));
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Images</h2>

                <h3>Simple Responsive Images</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
img {
    max-width: 100%;
    height: auto;
    display: block;
}
</style>

<img src="photo.jpg" alt="Description">`} />
                </div>

                <h3>Art Direction with Picture Element</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <!-- Desktop: landscape image --&gt;
    <source media="(min-width: 1024px)" srcset="desktop-wide.jpg">
    
    <!-- Tablet: medium image --&gt;
    <source media="(min-width: 768px)" srcset="tablet-medium.jpg">
    
    <!-- Mobile: portrait image --&gt;
    <img src="mobile-portrait.jpg" alt="Responsive image">
</picture>`} />
                </div>

                <h3>Resolution Switching with srcset</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="image-400.jpg"
     srcset="image-400.jpg 400w,
             image-800.jpg 800w,
             image-1200.jpg 1200w"
     sizes="(max-width: 600px) 100vw,
            (max-width: 900px) 50vw,
            33vw"
     alt="Responsive image">

<!-- Browser automatically picks best image based on:
   - Device screen size
   - Device pixel ratio
   - sizes attribute hints --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Container Queries (Modern)</h2>
                <p>
                    Container queries allow elements to respond to their container's size, not just viewport size:
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
/* Define container */
.card-container {
    container-type: inline-size;
    container-name: card;
}

.card {
    padding: 20px;
}

/* Query based on container width, not viewport */
@container card (min-width: 400px) {
    .card {
        display: grid;
        grid-template-columns: 1fr 2fr;
    }
}

@container card (min-width: 600px) {
    .card h2 {
        font-size: 2rem;
    }
}
</style>

<!-- Card adapts to container size --&gt;
<div class="card-container">
    <div class="card">
        <img src="photo.jpg" alt="Photo">
        <div>
            <h2>Title</h2>
            <p>Content...</p>
        </div>
    </div>
</div>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Navigation Patterns</h2>

                <h3>Hamburger Menu (Mobile)</h3>
                <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        nav {
            background: #333;
            color: white;
        }
        
        .menu-toggle {
            display: none;
            background: none;
            border: none;
            color: white;
            font-size: 24px;
            padding: 15px;
            cursor: pointer;
        }
        
        .nav-links {
            list-style: none;
            display: flex;
            gap: 20px;
            padding: 0;
            margin: 0;
        }
        
        .nav-links a {
            color: white;
            text-decoration: none;
            padding: 15px 20px;
            display: block;
        }
        
        /* Mobile styles */
        @media (max-width: 768px) {
            .menu-toggle {
                display: block;
            }
            
            .nav-links {
                display: none;
                flex-direction: column;
                position: absolute;
                top: 60px;
                left: 0;
                right: 0;
                background: #333;
            }
            
            .nav-links.active {
                display: flex;
            }
        }
    </style>
</head>
<body>
    <nav>
        <button class="menu-toggle" onclick="toggleMenu()"></button>
        <ul class="nav-links" id="navLinks">
            <li><a href="#home">Home</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#services">Services</a></li>
            <li><a href="#contact">Contact</a></li>
        </ul>
    </nav>

    <script>
    function toggleMenu() {
        document.getElementById('navLinks').classList.toggle('active');
    }
    </script>
</body>
</html>`} />
            </div>

            <div className="content-section">
                <h2>Responsive Tables</h2>

                <h3>Horizontal Scroll (Simple)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
.table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

table {
    min-width: 600px; /* Prevent squishing */
    width: 100%;
}
</style>

<div class="table-wrapper">
    <table>
        <thead>
            <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>John Doe</td>
                <td>john@example.com</td>
                <td>555-1234</td>
                <td>123 Main St</td>
            </tr>
        </tbody>
    </table>
</div>`} />
                </div>

                <h3>Stacked Layout (Mobile)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
table {
    width: 100%;
}

@media (max-width: 768px) {
    /* Hide thead */
    thead {
        display: none;
    }
    
    /* Stack rows */
    tr {
        display: block;
        margin-bottom: 20px;
        border: 1px solid #ddd;
    }
    
    /* Display cells as blocks with labels */
    td {
        display: block;
        text-align: right;
        padding: 10px;
        border-bottom: 1px solid #ddd;
    }
    
    td::before {
        content: attr(data-label);
        float: left;
        font-weight: bold;
    }
}
</style>

<table>
    <thead>
        <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <td data-label="Name">John Doe</td>
            <td data-label="Email">john@example.com</td>
            <td data-label="Phone">555-1234</td>
        </tr>
    </tbody>
</table>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Responsive Design</h2>

                <h3>Browser DevTools</h3>
                <ul>
                    <li><strong>Chrome:</strong> F12  Toggle Device Toolbar (Ctrl+Shift+M)</li>
                    <li><strong>Firefox:</strong> F12  Responsive Design Mode (Ctrl+Shift+M)</li>
                    <li><strong>Safari:</strong> Develop  Enter Responsive Design Mode</li>
                </ul>

                <h3>Testing Checklist</h3>
                <ul>
                    <li> Test all breakpoints (320px, 375px, 768px, 1024px, 1920px)</li>
                    <li> Test landscape and portrait orientations</li>
                    <li> Test with different pixel ratios (1x, 2x, 3x)</li>
                    <li> Test touch interactions on real devices</li>
                    <li> Test with different font sizes</li>
                    <li> Test with slow network (throttling)</li>
                    <li> Test accessibility (screen readers, keyboard navigation)</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Performance Optimization</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Load appropriate images --&gt;
<picture>
    <source media="(min-width: 1024px)" srcset="large.jpg">
    <source media="(min-width: 768px)" srcset="medium.jpg">
    <img src="small.jpg" loading="lazy" alt="Image">
</picture>

<!-- Lazy load images --&gt;
<img src="image.jpg" loading="lazy" alt="Description">

<!-- Preload critical resources --&gt;
<link rel="preload" href="critical.css" as="style">
<link rel="preload" href="hero-image.jpg" as="image">

<!-- Use modern image formats --&gt;
<picture>
    <source type="image/webp" srcset="image.webp">
    <source type="image/jpeg" srcset="image.jpg">
    <img src="image.jpg" alt="Image">
</picture>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>

                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use mobile-first approach</li>
                            <li>Test on real devices</li>
                            <li>Use relative units (%, rem, em)</li>
                            <li>Optimize images for each breakpoint</li>
                            <li>Keep touch targets 48x48px minimum</li>
                            <li>Use semantic HTML</li>
                            <li>Progressive enhancement</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use fixed pixel widths</li>
                            <li>Hide content on mobile unnecessarily</li>
                            <li>Use tiny text (&lt; 16px)</li>
                            <li>Forget to test touch interactions</li>
                            <li>Load desktop images on mobile</li>
                            <li>Disable zoom</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLResponsiveDesign;





